/**
 * Cliente del CRUD de `analyze_config` (Fase 41).
 *
 * - GET    /admin/analyze/config         → { config, defaults, updatedAt, updatedBy }
 * - PATCH  /admin/analyze/config         → reemplaza la config entera
 * - GET    /admin/analyze/config/audit   → historial append-only
 *
 * Cambios surten efecto en la próxima sesión (no afectan jobs ya encolados).
 */
import { get, patchJsonAdmin } from './_core.js';

export type CriticStrictness = 'strict' | 'balanced' | 'permissive';

export interface AutoTriageRule {
  category?: string;
  maxSeverity?: 1 | 2 | 3 | 4 | 5;
  module?: string;
  action: 'reject' | 'duplicate';
  reason?: string;
}

export interface AnalyzeConfig {
  enabled: boolean;
  minTurnCount: number;
  minDurationSeconds: number;
  analyzeSynthetic: boolean;
  budgetMonthlyUsd: number;
  analyzeSampleRate: number;
  readerMaxIterations: number;
  maxFindingsPerRun: number;
  matcherEnabled: boolean;
  criticStrictness: CriticStrictness;
  mutedCategories: string[];
  autoTriageRules: AutoTriageRule[];
  plannerModel: string;
  readerModel: string;
  criticModel: string;
  matcherModel: string;
}

/** Modelo elegible para los selects (anthropic + LLM, del catálogo estático). */
export interface AnalyzeModelOption {
  id: string;
  displayName: string;
}

export interface AnalyzeConfigResponse {
  config: AnalyzeConfig;
  defaults: AnalyzeConfig;
  modelOptions: AnalyzeModelOption[];
  updatedAt: string;
  updatedBy: string | null;
}

export interface AnalyzeConfigAuditEntry {
  id: string;
  changedAt: string;
  changedByUserId: string | null;
  beforeConfig: AnalyzeConfig | null;
  afterConfig: AnalyzeConfig;
}

export async function fetchAnalyzeConfig(): Promise<AnalyzeConfigResponse> {
  const res = await get<AnalyzeConfigResponse>('/admin/analyze/config');
  return res.data;
}

export async function patchAnalyzeConfig(
  config: AnalyzeConfig,
): Promise<{ config: AnalyzeConfig }> {
  return patchJsonAdmin<{ config: AnalyzeConfig }>('/admin/analyze/config', { config });
}

export async function fetchAnalyzeConfigAudit(): Promise<{
  entries: AnalyzeConfigAuditEntry[];
}> {
  const res = await get<{ entries: AnalyzeConfigAuditEntry[] }>('/admin/analyze/config/audit');
  return res.data;
}
